import React, { useState, useRef, useEffect, useCallback } from 'react';
import './App.css';
import { getLanguageTemplate, isSupportedLanguage, getDefaultLanguage } from './utils/languageUtils';
import Header from './components/Header/Header';
import EditorWithBus from './components/Editor/EditorWithBus';
import ConfigPanel from './components/ConfigPanel/ConfigPanel';
import ChatWindow from './components/ChatWindow/ChatWindow';
import AssistantButton from './components/ChatWindow/AssistantButton';
import eventBus from './utils/eventBus';
import { useAuth } from './contexts/AuthContext';

const MIN_CONFIG_WIDTH = 200;
const DEFAULT_CONFIG_WIDTH = 450;

function App() {
  // 获取认证上下文
  const auth = useAuth();
  const [configPanelWidth, setConfigPanelWidth] = useState(DEFAULT_CONFIG_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const appRef = useRef<HTMLDivElement>(null); // Ref for the main app container to get its width
  
  // 添加共享状态
  const [language, setLanguage] = useState<string>(getDefaultLanguage());
  const { code: defaultCode, fileName: defaultFileName } = getLanguageTemplate(getDefaultLanguage());
  const [code, setCode] = useState<string>(defaultCode);
  const [fileName, setFileName] = useState<string>(defaultFileName);
  const [stdin, setStdin] = useState<string>('');
  const [output, setOutput] = useState<string>('');
  const [compilerOptions, setCompilerOptions] = useState<string>('');
  const [isCompiling, setIsCompiling] = useState<boolean>(false);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  
  // AI 助手相关状态
  const [showAssistant, setShowAssistant] = useState<boolean>(false);
  const [chatMessages, setChatMessages] = useState<{ role: 'user' | 'assistant'; content: string }[]>([
    { role: 'assistant', content: '您好！我是 AI 编程助手，有什么可以帮助您的？' }
  ]);
  const [inputValue, setInputValue] = useState<string>('');

  // 从URL获取语言参数
  useEffect(() => {
    if (!auth.isAuthenticated) return;
    
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get('language');
    
    if (langParam && isSupportedLanguage(langParam)) {
      handleLanguageChange(langParam);
      
      // 从URL中移除language参数
      urlParams.delete('language');
      const newUrl = window.location.pathname + 
                    (urlParams.toString() ? `?${urlParams.toString()}` : '') + 
                    window.location.hash;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [auth.isAuthenticated]);
  
  // 切换语言时更新默认代码和文件名
  const handleLanguageChange = (newLanguage: string) => {
    // 只有当选择了有效的语言且与当前语言不同时才更新
    if (newLanguage && newLanguage !== language && isSupportedLanguage(newLanguage)) {
      const { code: newCode, fileName: newFileName } = getLanguageTemplate(newLanguage);
      setLanguage(newLanguage);
      setCode(newCode);
      setFileName(newFileName);
      
      // 清空之前的输出和状态
      setOutput('');
      setStdin('');
      setIsCompiling(false);
      setIsRunning(false);
    }
  };
  
  // 处理发送消息给 AI 助手
  const handleSendMessage = (message: string) => {
    // 添加用户消息
    const updatedMessages = [...chatMessages, { role: 'user' as const, content: message }];
    setChatMessages(updatedMessages);
    setInputValue('');
    
    // 模拟 AI 回复（实际项目中应该调用 AI API）
    setTimeout(() => {
      setChatMessages([...updatedMessages, { 
        role: 'assistant' as const, 
        content: `我收到了您的消息：${message}

这是一个模拟回复，实际项目中应该连接到 AI API。` 
      }]);
    }, 1000);
  };
  
  // 切换 AI 助手显示状态
  const toggleAssistant = () => {
    setShowAssistant(!showAssistant);
  };
  
  // 监听显示 AI 助手的事件
  useEffect(() => {
    const handleShowAiAssistant = () => {
      setShowAssistant(true);
    };
    
    // 注册事件监听器
    eventBus.on('showAiAssistant', handleShowAiAssistant);
    
    // 清理函数
    return () => {
      eventBus.off('showAiAssistant', handleShowAiAssistant);
    };
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing || !appRef.current) return;
      // Calculate new width based on mouse position relative to the right edge of the app container
      const appRect = appRef.current.getBoundingClientRect();
      let newWidth = appRect.right - e.clientX;
      
      // Apply constraints
      if (newWidth < MIN_CONFIG_WIDTH) {
        newWidth = MIN_CONFIG_WIDTH;
      }
      // Max width can be a percentage of app width, e.g., 50%
      const MAX_CONFIG_WIDTH = appRect.width * 0.7;
      if (newWidth > MAX_CONFIG_WIDTH) {
        newWidth = MAX_CONFIG_WIDTH;
      }
      setConfigPanelWidth(newWidth);
    },
    [isResizing]
  );

  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // 处理认证状态
  if (auth.isLoading) {
    return (
      <div className="auth-loading">
        <div className="loading-spinner"></div>
        <p>正在加载中，请稍候...</p>
      </div>
    );
  }
  
  if (!auth.isAuthenticated) {
    return (
      <div className="auth-error">
        <h2>认证失败</h2>
        <p>无法验证您的身份，请检查链接是否有效或已过期。</p>
        {auth.error && <p className="error-message">{auth.error}</p>}
      </div>
    );
  }

  return (
    <div className="App" ref={appRef}>
      <Header language={language} />
      <div className="main-container">
        <div className="editor-section">
          <EditorWithBus 
            code={code} 
            setCode={setCode} 
            language={language} 
            setLanguage={handleLanguageChange} 
            fileName={fileName} 
            setFileName={setFileName}
            isCompiling={isCompiling}
            isRunning={isRunning}
          />
        </div>
        <div 
          className="resizer" 
          onMouseDown={handleMouseDown}
        />
        <div 
          className="config-section"
          style={{ width: `${configPanelWidth}px` }}
        >
          <ConfigPanel 
            code={code} 
            language={language} 
            stdin={stdin} 
            setStdin={setStdin} 
            output={output} 
            setOutput={setOutput} 
            compilerOptions={compilerOptions} 
            setCompilerOptions={setCompilerOptions}
            isCompiling={isCompiling}
            setIsCompiling={setIsCompiling}
            isRunning={isRunning}
            setIsRunning={setIsRunning}
          />
        </div>
        
        {/* AI 助手浮窗 */}
        {showAssistant ? (
          <ChatWindow
            visible={showAssistant}
            messages={chatMessages}
            inputValue={inputValue}
            onInputChange={setInputValue}
            onSend={handleSendMessage}
            onClose={toggleAssistant}
            typingSpeed={25} // 设置打字速度，可以根据需要调整
          />
        ) : (
          <AssistantButton onClick={toggleAssistant} />
        )}
      </div>
    </div>
  );
}

export default App;
